import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email }, { withCredentials: true });
      setDone(true);
    } catch {
      // Same UX whether the address exists or not — never leak enumeration.
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] flex flex-col">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 flex justify-between items-center">
          <Link to="/"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-3"><DarkModeToggle /><LanguageSwitcher variant="minimal" /></div>
        </div>
      </nav>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
          <h1 data-testid="forgot-title" className="font-primary font-bold text-2xl md:text-3xl text-slate-900 dark:text-white mb-2">
            {t('auth.forgot.title')}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{t('auth.forgot.subtitle')}</p>

          {done ? (
            <div data-testid="forgot-done" className="text-sm text-slate-700 dark:text-slate-200 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              {t('auth.forgot.generic')}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input id="email" data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={submitting}
                className="w-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
                {submitting ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-slate-600 dark:text-slate-400">
            <Link to="/login" className="text-[#45C04A] underline">{t('auth.forgot.backToLogin')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
